<template>
  <div class="cma-list-view container">
    <!-- breadcrumb -->
    <ol class="breadcrumb arr-right mb-0 mb-md-3 mt-5">
      <li class="breadcrumb-item">
        <router-link :to="{ name: 'cmaList' }" class="headings">{{
          $t('cmaList.index.title')
        }}</router-link>
      </li>
      <li class="breadcrumb-item reference-heading heading-color">
        {{ $t('cmaList.index.list') }}
      </li>
    </ol>
    <!-- breadcrumb end-->

    <!-- jumbotron -->
    <div class="hero-section jumbotron">
      <div class="d-flex flex-column align-items-center">
        <h5 class="text-white text-center mb-5">
          {{ $t('cmaList.index.desc') }}
        </h5>
        <!-- tab menu -->
        <nav class="state-menu nav nav-pills flex-row justify-content-center">
          <a
            v-for="(state, i) in states"
            :key="i"
            class="nav-link text-center"
            :class="{ active: i === selectedState }"
            @click="selectState(i)"
            href="#"
            >{{ i }}</a
          >
        </nav>
      </div>
    </div>
    <!-- jumbotron ends -->

    <!-- expansion panels -->
    <div class="accordion-section">
      <b-card
        class="mb-3"
        no-body
        v-for="(state, i) in states[selectedState]"
        :key="i"
      >
        <summary
          header-tag="header"
          class="py-3 px-4 px-sm-5 d-flex justify-content-between align-items-center"
          role="tab"
          v-b-toggle="`accordion-${i}`"
          variant="info"
        >
          <div class="city-container">
            <h5 class="m-0 mr-4" :class="{ 'text-primary': collapseStates[i] }">
              {{ state.city.split('-')[1] }}
            </h5>
            <chip :chipText="state.city.split('-')[0]" />
          </div>
          <h4 class="m-0">
            <b-icon-chevron-down
              font-scale="0.6"
              :class="{ 'text-primary': collapseStates[i] }"
              v-show="!collapseStates[i]"
            />
            <b-icon-chevron-up
              font-scale="0.6"
              :class="{ 'text-primary': collapseStates[i] }"
              v-show="collapseStates[i]"
            />
          </h4>
        </summary>

        <b-collapse
          :id="`accordion-${i}`"
          accordion="city-accordion"
          role="tabpanel"
          v-model="collapseStates[i]"
        >
          <b-card-body class="py-0">
            <b-row class="border-top" v-for="(data, i) in detailsMap" :key="i">
              <!-- label col (desktop only) -->
              <b-col
                :cols="3"
                class="d-none d-sm-flex border-right px-4 px-sm-5 py-4"
              >
                <div class="m-0 mr-4"><component :is="data.icon" /></div>
                {{ data.label }}
              </b-col>
              <!-- details col (desktop & mobile) -->
              <b-col :cols="12" :sm="9" class="d-flex py-4 px-4 px-sm-5">
                <h5 class="d-sm-none m-0 mr-4">
                  <component :is="data.icon" />
                </h5>
                <!-- address data -->
                <template
                  v-if="data.dataKey === $t('cmaList.DATA_KEYS.ADDRESS')"
                >
                  <summary
                    block
                    @click="copyToClipBoard(state.data[data.dataKey])"
                    class="address-field d-flex pl-sm-4 pr-5 py-sm-3 rounded w-100 position-relative"
                  >
                    {{ state.data[data.dataKey] }}
                    <b-icon-files class="copy-icon position-absolute" />
                  </summary>
                </template>
                <!-- other data -->
                <template v-else>{{ state.data[data.dataKey] }}</template>
              </b-col>
            </b-row>
          </b-card-body>
        </b-collapse>
      </b-card>
    </div>
    <!-- expansion panels end -->
  </div>
</template>

<script>
import {
  BIconChevronDown,
  BIconChevronUp,
  BIconFilePersonFill,
  BIconTelephoneFill,
  BIconEnvelopeFill,
  BIconBuilding,
  BIconGeoAltFill,
  BIconFiles
} from 'bootstrap-vue'
import Chip from '@/components/common/Chip'

const DEFAULT_COLLAPSE = [true]

export default {
  components: {
    BIconChevronDown,
    BIconChevronUp,
    BIconFilePersonFill,
    BIconTelephoneFill,
    BIconEnvelopeFill,
    BIconBuilding,
    BIconGeoAltFill,
    BIconFiles,
    Chip
  },
  data() {
    return {
      collapseStates: [...DEFAULT_COLLAPSE],
      selectedState: '',
      detailsMap: [
        {
          label: 'Name',
          icon: BIconFilePersonFill,
          dataKey: this.$t('cmaList.DATA_KEYS.PERSON')
        },
        {
          label: 'Contact',
          icon: BIconTelephoneFill,
          dataKey: this.$t('cmaList.DATA_KEYS.MOBILE')
        },
        {
          label: 'Email',
          icon: BIconEnvelopeFill,
          dataKey: this.$t('cmaList.DATA_KEYS.EMAIL')
        },
        {
          label: 'CMA Company',
          icon: BIconBuilding,
          dataKey: this.$t('cmaList.DATA_KEYS.COMPANY')
        },
        {
          label: 'Address',
          icon: BIconGeoAltFill,
          dataKey: this.$t('cmaList.DATA_KEYS.ADDRESS')
        }
      ],
      states: {}
    }
  },
  watch: {
    selectedState: function () {
      this.collapseStates = [...DEFAULT_COLLAPSE]
    }
  },
  mounted() {
    this.$mpTrack('Cma list page mounted')
    this.getCmaData()
  },
  methods: {
    selectState(i) {
      this.$mpTrack(`CMA List - State selected ${i}`)
      this.selectedState = i
    },
    async getCmaData() {
      try {
        const {
          VUE_APP_CTF_SPACE_ID,
          VUE_APP_CTF_ENVIRONMENT,
          VUE_APP_CTF_TOKEN
        } = process.env
        const res = await fetch(
          `https://cdn.contentful.com/spaces/${VUE_APP_CTF_SPACE_ID}/environments/${VUE_APP_CTF_ENVIRONMENT}/entries?access_token=${VUE_APP_CTF_TOKEN}&content_type=cmaListing`
        )
        const entries = await res.json()
        const bannerTitle = 'List'
        this.bannerData = entries.items.find(
          (item) => item.fields.title === bannerTitle
        )
        if (this.bannerData && this.bannerData.fields) {
          this.states = this.bannerData.fields.data
          this.selectedState = Object.keys(this.states)[0]
        }
      } catch (e) {
        console.log(e)
      }
    },
    copyToClipBoard(text) {
      navigator.clipboard.writeText(text)
      this.$notify({
        title: this.$t('notifications.copyMsg'),
        type: 'success'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables';
.cma-list-view {
  .hero-section {
    background-image: url('../../assets/images/hero/cma-list.png');
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;

    .state-menu {
      max-width: 40rem;

      .nav-link {
        border-radius: 2rem;
        background: $white;
        font-size: 0.75rem;
        color: $black;
        margin: 0 0.625rem 0.625rem 0;

        &.active {
          background: $primary;
          color: $white;
        }
      }
    }
  }

  .city-container {
    display: flex;
  }

  .copy-icon {
    top: 0;
    right: 0;
  }

  @include media-breakpoint-up(sm) {
    .hero-section {
      .state-menu {
        .nav-link {
          font-size: 1rem;
          margin: 0 0.75rem 0.75rem 0;
        }
      }
    }

    .address-field {
      background-color: $light-background-color;
      left: -1.5rem;
    }

    .copy-icon {
      top: 10px;
      right: 10px;
    }
  }
}
</style>
